
	import Vue from 'vue';
	import Component from 'vue-class-component';

	import { mdiAttachmentCheck, mdiAttachmentPlus, mdiDownload, mdiPaperclip } from '@mdi/js';

	// Utils from Vue Dot
	import { calcHumanFileSize } from '@cnamts/vue-dot/src/functions/calcHumanFileSize';

	import { Refs } from '@cnamts/vue-dot/src/types';

	const Props = Vue.extend({
		props: {
			/**
			 * The v-model,
			 * can be a single file or an array of files
			 */
			value: {
				type: [Array, Object, File],
				default: () => []
			},
			/** Label of the input */
			label: {
				type: String,
				default: null
			},
			name: {
				type: String,
				default: 'file-input'
			},
			/** Label of the input */
			filename: {
				type: String,
				default: null
			},
			/** Size units used in the template for i18n */
			fileSizeUnits: {
				type: [Array, Object],
				default: () => ['o', 'Ko', 'Mo', 'Go', 'To']
			},
			/** Vuetify validation rules of the input */
			rules: {
				type: [Array, Object],
				default: () => []
			},
			/** Options of the Vuetify VFileInput */
			fileInputOptions: {
				type: [Array, Object],
				default: () => ({
					persistentHint: true,
					outlined: true
				})
			},
			/**
			 * Maximum size in bytes per file,
			 * default 10485760 bytes => 10Mo
			 */
			fileSizeMax: {
				type: Number,
				default: 10485760
			},
			/** Allowed file extensions */
			allowedExtensions: {
				type: [Array, Object],
				default: () => ['PDF']
			},
			accept: {
				type: String,
				default: 'application/pdf,.pdf,application/x-pdf'
			},
			deletableFile: {
				type: Boolean,
				default: true
			},
			uploadableFile: {
				type: Boolean,
				default: true
			},
			downloadableFile: {
				type: Boolean,
				default: true
			},
			downloading: {
				type: Boolean,
				default: false
			}
		}
	});

	/** Extend the VFileInput from Vuetify */
	@Component({
		// v-model
		model: {
			prop: 'value',
			event: 'change'
		}
	})
	export default class FileInput extends Props {

		$refs!: Refs<{
			fileInput: HTMLFormElement;
		}>;

		paperclipIcon = mdiPaperclip;
		attachmentInputIcon = mdiAttachmentPlus;
		attachmentIcon = mdiAttachmentCheck;
		downloadIcon = mdiDownload;
		showFileInput = false;

		/** Computed extensions for display */
		get extensions(): string {
			return this.allowedExtensions.join(', ');
		}

		/** Compute maximum size to human readable */
		get maxSizeReadable(): string {
			return calcHumanFileSize(this.fileSizeMax, this.fileSizeUnits);
		}

		/** Hint below the input, composed of max size & extensions */
		get hint(): string {
			const maxSize = this.$t('components.globals.fileInput.messages.maxSize', {
				maxSizeReadable: this.maxSizeReadable
			});

			const extensions = this.$tc(
				'components.globals.fileInput.messages.accept',
				this.allowedExtensions.length,
				{
					extensions: this.extensions
				}
			);

			return `${maxSize} ${extensions}`;
		}

		get isDownloadableFile(): boolean {
			return this.downloadableFile && false === this.value instanceof File;
		}

		get internalModel(): File | File[] {
			return this.value;
		}

		set internalModel(value: File | File[]) {
			this.$emit('change', value);
		}

		get displayInput(): boolean {
			return (null === this.filename  && this.uploadableFile) || this.value instanceof File;
		}

		mounted() {
			// if the filename is not defined, we show the FileInput component
			if (null === this.filename) {
				this.showFileInput = true;
			}
		}

		// On textfield click event, we click on the input file
		clickInputFile(): void {
			const inputEl = this.$refs.fileInput.$el.querySelector('input');
			inputEl.click();
		}

		deleteFile(event: any): void {
			this.$emit('delete-file', { closeChip: true, inputKey: event });
		}

		downloadFile(event: any): void {
			this.$emit('download-file',  event);
		}
	}
